import type { UpdateDriverInput } from '@chukua/common';
import { Alert, Button, DatePicker, Form, Input, Select, Switch } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useEffect } from 'react';
import type { DriverView } from '../drivers.api';

interface DetailsFormValues {
  name: string;
  phone: string;
  nationalId: string;
  dateOfBirth: Dayjs;
  licenceNumber: string;
  licenceClasses: string[];
  hazmatCertified: boolean;
}

interface DriverDetailsFormProps {
  driver: DriverView;
  canManage: boolean;
  error: string | null;
  saving: boolean;
  onSave: (input: UpdateDriverInput) => Promise<boolean>;
}

function toFormValues(driver: DriverView): DetailsFormValues {
  return {
    name: driver.name,
    phone: driver.phone,
    nationalId: driver.nationalId,
    dateOfBirth: dayjs(driver.dateOfBirth),
    licenceNumber: driver.licenceNumber,
    licenceClasses: driver.licenceClasses,
    hazmatCertified: driver.hazmatCertified,
  };
}

function toUpdateInput(values: DetailsFormValues): UpdateDriverInput {
  return {
    name: values.name,
    phone: values.phone,
    nationalId: values.nationalId,
    dateOfBirth: values.dateOfBirth.format('YYYY-MM-DD'),
    licenceNumber: values.licenceNumber,
    licenceClasses: values.licenceClasses,
    hazmatCertified: values.hazmatCertified,
  };
}

/** The driver's fields, editable inline for `driver.manage` holders — read-only for everyone else with `driver.view` (CLAUDE.md: nav-hiding and disabled controls are UX only, the server remains the authority). Personal fields (national ID, date of birth) are still shown here — this screen itself is behind `driver.view`, the access-restriction CLAUDE.md → Data protection asks for. */
export function DriverDetailsForm({ driver, canManage, error, saving, onSave }: DriverDetailsFormProps) {
  const [form] = Form.useForm<DetailsFormValues>();

  useEffect(() => {
    form.setFieldsValue(toFormValues(driver));
  }, [driver, form]);

  async function submit(values: DetailsFormValues) {
    await onSave(toUpdateInput(values));
  }

  return (
    <Form<DetailsFormValues>
      form={form}
      layout="vertical"
      requiredMark={false}
      onFinish={submit}
      disabled={!canManage || saving}
      initialValues={toFormValues(driver)}
    >
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <div className="grid grid-cols-2 gap-3">
        <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Enter a name' }]}>
          <Input />
        </Form.Item>
        <Form.Item
          name="phone"
          label="Phone"
          rules={[{ required: true, pattern: /^\+254(7|1)\d{8}$/, message: 'Expected a Kenyan mobile number in +254 format' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="nationalId" label="National ID / passport number" rules={[{ required: true, message: 'Enter a national ID or passport number' }]}>
          <Input />
        </Form.Item>
        <Form.Item name="dateOfBirth" label="Date of birth" rules={[{ required: true, message: 'Select a date of birth' }]}>
          <DatePicker className="w-full" />
        </Form.Item>
        <Form.Item name="licenceNumber" label="Licence number" rules={[{ required: true, message: 'Enter a licence number' }]}>
          <Input />
        </Form.Item>
        <Form.Item name="licenceClasses" label="Licence class(es)" rules={[{ required: true, message: 'Enter at least one licence class' }]}>
          <Select mode="tags" placeholder="e.g. BCE" />
        </Form.Item>
      </div>
      <Form.Item name="hazmatCertified" label="Hazardous materials certified" valuePropName="checked">
        <Switch />
      </Form.Item>
      {canManage && (
        <Form.Item className="mb-0">
          <Button type="primary" htmlType="submit" loading={saving}>
            Save changes
          </Button>
        </Form.Item>
      )}
    </Form>
  );
}
